import React from 'react';
import { useShow, useDelete, useNavigation } from '@refinedev/core';
import { Show, ListButton } from '@refinedev/antd';
import {
  Descriptions,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Alert,
  Card,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  ReloadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { Sandbox } from '../../types/sandbox';
import { StatusBadge } from '../../components/sandboxes/StatusBadge';
import { URLDisplay } from '../../components/sandboxes/URLDisplay';

const { Text } = Typography;

export const SandboxShow: React.FC = () => {
  const { queryResult } = useShow<Sandbox>();
  const { data, isLoading, refetch } = queryResult;
  const sandbox = data?.data;
  const { mutate: deleteSandbox } = useDelete();
  const { list } = useNavigation();

  // Handle refresh
  const handleRefresh = () => {
    refetch();
  };

  // Handle delete with confirmation
  const handleDelete = () => {
    if (!sandbox) return;

    Modal.confirm({
      title: 'Delete Sandbox',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to delete sandbox <strong>{sandbox.id}</strong>?</p>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        deleteSandbox(
          {
            resource: 'sandboxes',
            id: sandbox.id,
          },
          {
            onSuccess: () => {
              list('sandboxes');
            },
          }
        );
      },
    });
  };

  // Calculate time since creation
  const getTimeSinceCreation = () => {
    if (!sandbox) return '';
    const created = new Date(sandbox.created_at);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  return (
    <Show
      isLoading={isLoading}
      headerButtons={({ listButtonProps }) => (
        <>
          <ListButton {...listButtonProps} />
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            Refresh
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            Delete
          </Button>
        </>
      )}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Status Alert */}
        {sandbox?.status === 'failed' && sandbox.status_message && (
          <Alert
            message="Sandbox Creation Failed"
            description={sandbox.status_message}
            type="error"
            showIcon
          />
        )}

        {/* Quick Stats */}
        <Row gutter={16}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Status"
                value={sandbox?.status || 'Unknown'}
                prefix={sandbox && <StatusBadge status={sandbox.status} />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Cluster"
                value={sandbox?.spec.cluster || 'N/A'}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Created"
                value={getTimeSinceCreation()}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Information */}
        <Card title="Basic Information">
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Sandbox ID">{sandbox?.id}</Descriptions.Item>

            <Descriptions.Item label="URL">
              {sandbox?.url && <URLDisplay url={sandbox.url} maxLength={60} />}
            </Descriptions.Item>

            <Descriptions.Item label="Created By">{sandbox?.created_by}</Descriptions.Item>

            <Descriptions.Item label="Created At">
              {sandbox && new Date(sandbox.created_at).toLocaleString()}
            </Descriptions.Item>

            <Descriptions.Item label="Updated At">
              {sandbox && new Date(sandbox.updated_at).toLocaleString()}
            </Descriptions.Item>

            {sandbox?.spec.ttl_seconds && (
              <Descriptions.Item label="TTL">
                {sandbox.spec.ttl_seconds} seconds (
                {Math.floor(sandbox.spec.ttl_seconds / 60)} minutes)
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Labels */}
        {sandbox?.spec.labels && Object.keys(sandbox.spec.labels).length > 0 && (
          <Card title="Labels">
            <Space wrap>
              {Object.entries(sandbox.spec.labels).map(([key, value]) => (
                <Tag key={key} color="blue">
                  {key}: {value}
                </Tag>
              ))}
            </Space>
          </Card>
        )}

        {/* Forks Configuration */}
        <Card title="Forked Resources">
          {sandbox?.spec.forks.map((fork, index) => (
            <Card
              key={index}
              type="inner"
              title={`Fork ${index + 1}`}
              style={{ marginBottom: index < sandbox.spec.forks.length - 1 ? 16 : 0 }}
            >
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Resource Type">
                  <Tag color="purple">{fork.fork_of.kind}</Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Namespace">
                  <Tag>{fork.fork_of.namespace}</Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Resource Name">
                  <Text code>{fork.fork_of.name}</Text>
                </Descriptions.Item>

                {fork.customizations.images && fork.customizations.images.length > 0 && (
                  <Descriptions.Item label="Custom Images">
                    {fork.customizations.images.map((img, imgIndex) => (
                      <div key={imgIndex}>
                        <Text code>{img.image}</Text>
                        {img.container_name && (
                          <Text type="secondary"> ({img.container_name})</Text>
                        )}
                      </div>
                    ))}
                  </Descriptions.Item>
                )}

                {fork.customizations.replicas !== undefined && (
                  <Descriptions.Item label="Replicas">
                    {fork.customizations.replicas}
                  </Descriptions.Item>
                )}

                {fork.customizations.env && fork.customizations.env.length > 0 && (
                  <Descriptions.Item label="Environment Variables">
                    {fork.customizations.env.map((env, envIndex) => (
                      <div key={envIndex}>
                        <Text code>
                          {env.name}={env.value}
                        </Text>
                      </div>
                    ))}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          ))}
        </Card>

        {/* Local Development Configuration */}
        {sandbox?.spec.local && (
          <Card title="Local Development Configuration">
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Service">
                <Text code>{sandbox.spec.local.service}</Text>
              </Descriptions.Item>

              <Descriptions.Item label="Port">
                <Tag color="green">{sandbox.spec.local.port}</Tag>
              </Descriptions.Item>

              {sandbox.spec.local.tunnel_endpoint && (
                <Descriptions.Item label="Tunnel Endpoint">
                  <Text code>{sandbox.spec.local.tunnel_endpoint}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        )}
      </Space>
    </Show>
  );
};
